import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, ZapIcon, DollarSignIcon, ActivityIcon, CheckIcon, NetworkIcon, GlobeIcon, WrenchIcon, ArrowRightIcon } from 'lucide-react';
const stats = [{
  label: 'RELIABILITY',
  value: '99.999%',
  desc: 'Ethernet networks offer an impressive uptime of up to 99.999%, ensuring that your business remains connected with minimal downtime.',
  icon: ActivityIcon,
  color: 'text-green-500',
  bg: 'bg-green-50'
}, {
  label: 'COST EFFECTIVE',
  value: 'Up to 50%',
  desc: 'Switching to Ethernet can reduce networking costs by up to 50% compared to older technologies like T1 lines, especially as bandwidth needs grow.',
  icon: DollarSignIcon,
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  label: 'SECURITY',
  value: '70%',
  desc: '70% of businesses report improved data security with Ethernet services, thanks to private, dedicated connections that minimize the risk of cyber threats.',
  icon: ShieldCheckIcon,
  color: 'text-purple-500',
  bg: 'bg-purple-50'
}, {
  label: 'SPEED',
  value: '100 Gbps',
  desc: 'Ethernet services can support data transfer speeds ranging from 1 Mbps to 100 Gbps, making them ideal for businesses of all sizes and needs.',
  icon: ZapIcon,
  color: 'text-amber-500',
  bg: 'bg-amber-50'
}];
const keyFeatures = ['Single Ethernet interface', 'VLAN Transparency', 'Scalable capacity', 'Secure, private connection', '24/7 priority support', 'Performance reporting', 'Symmetrical connectivity'];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4
    }
  }
};
export function EthernetFiberPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div data-id="element-640">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-641">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-642"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-643"></div>
        <div className="absolute top-0 left-1/3 -translate-y-1/4 w-[700px] h-[700px] bg-purple-500/15 rounded-full blur-[120px] pointer-events-none" data-id="element-644"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-645">
          <div className="max-w-3xl" data-id="element-646">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-647">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-648">
                <span className="flex h-2 w-2 rounded-full bg-purple-400 mr-2" data-id="element-649"></span>
                Dedicated High-Performance Networking
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-650">
                Ethernet – Fiber
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8" data-id="element-651">
                Enterprise-grade Ethernet solutions with dedicated bandwidth,
                symmetrical speeds, and 99.999% uptime — built for businesses
                that can't afford to slow down.
              </p>
              <button onClick={() => onNavigate('home')} className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-652">
                Get a Free Analysis
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-653" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-white" data-id="element-654">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-655">
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} data-id="element-656">
            {stats.map((stat, i) => {
            const Icon = stat.icon;
            return <motion.div key={i} variants={itemVariants} className="text-center" data-id="element-657">
                  <div className={`w-14 h-14 ${stat.bg} rounded-2xl flex items-center justify-center mx-auto mb-4`} data-id="element-658">
                    <Icon className={`w-7 h-7 ${stat.color}`} data-id="element-659" />
                  </div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2" data-id="element-660">
                    {stat.label}
                  </div>
                  <div className="text-3xl font-extrabold text-slate-900 mb-3" data-id="element-661">
                    {stat.value}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-662">
                    {stat.desc}
                  </p>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* Key Features + Description */}
      <section className="py-20 bg-slate-50" data-id="element-663">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-664">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-665">
            {/* Key Features */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-666">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8" data-id="element-667">
                Key Features
              </h2>
              <div className="space-y-4" data-id="element-668">
                {keyFeatures.map((feature, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.3,
                delay: i * 0.05
              }} className="flex items-center bg-white rounded-xl px-5 py-4 border border-slate-100 shadow-sm" data-id="element-669">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mr-4" data-id="element-670">
                      <CheckIcon className="w-4 h-4 text-primary" data-id="element-671" />
                    </div>
                    <span className="text-slate-800 font-medium" data-id="element-672">
                      {feature}
                    </span>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Description */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="lg:pt-4" data-id="element-673">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-674">
                Enterprise-Grade Networking
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-675">
                Ethernet Network Services provide businesses with a robust,
                high-performance networking solution tailored to the needs of
                modern enterprises. Whether your goal is to link multiple office
                locations, support critical cloud applications, or ensure smooth
                communication across your organization, our Ethernet services
                deliver the reliability and scalability required to keep your
                business running efficiently.
              </p>
              <div className="relative rounded-2xl overflow-hidden shadow-xl" data-id="element-676">
                <img src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&h=500&fit=crop" alt="Fiber optic cables" className="w-full h-auto object-cover" data-id="element-677" />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/30 to-transparent" data-id="element-678"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Ethernet */}
      <section className="py-20 bg-white" data-id="element-679">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-680">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-681">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-682">
              Why Ethernet Network Services?
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed" data-id="element-683">
              Ethernet Network Services offer your business unmatched speed,
              reliability, and security. With up to 10x faster data transfer
              rates, an impressive 99.999% uptime, and robust, private
              connections, Ethernet solutions ensure seamless operations and
              top-notch data protection. Scalable and cost-effective, they also
              enhance global connectivity by up to 40%, supporting your business
              growth and collaboration.
            </p>
          </motion.div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-684">
            {[{
            label: '10x faster',
            desc: 'Data transfer rates compared to legacy solutions'
          }, {
            label: '99.999%',
            desc: 'Network uptime guarantee'
          }, {
            label: '40% boost',
            desc: 'In global connectivity and collaboration'
          }, {
            label: 'Private',
            desc: 'Dedicated connections for maximum security'
          }].map((item, i) => <motion.div key={i} variants={itemVariants} className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-id="element-685">
                <div className="text-2xl font-extrabold text-primary mb-2" data-id="element-686">
                  {item.label}
                </div>
                <p className="text-slate-600" data-id="element-687">{item.desc}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Ambient Telecom */}
      <section className="py-20 bg-slate-50" data-id="element-688">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-689">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-690">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-691">
              Why Choose Ambient Telecom?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-692">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm" data-id="element-693">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5" data-id="element-694">
                <GlobeIcon className="w-6 h-6 text-primary" data-id="element-695" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-696">
                Global Reach
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-697">
                Providing a comprehensive range of connectivity solutions with a
                global reach, ensuring your business stays connected no matter
                where you operate.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm" data-id="element-698">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5" data-id="element-699">
                <WrenchIcon className="w-6 h-6 text-primary" data-id="element-700" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-701">
                Custom Solutions
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-702">
                Developing custom solutions tailored specifically to meet your
                unique business requirements, ensuring you get exactly the
                connectivity you need.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary relative overflow-hidden py-20" data-id="element-703">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-704">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl" data-id="element-705"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl" data-id="element-706"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" data-id="element-707">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-708">
            Ready to upgrade to Ethernet-Fiber?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-709">
            Let our experts analyze your current setup and show you how
            dedicated Ethernet can reduce costs while boosting performance.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-710">
            <button onClick={() => onNavigate('home')} className="w-full sm:w-auto bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-711">
              Get Your Free Analysis
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-712" />
            </button>
            <a href="tel:+4105255284" className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg transition-colors" data-id="element-713">
              Call Us: +410-525-5284
            </a>
          </div>
        </div>
      </section>
    </div>;
}